<template>
    <nav class="flex items-center justify-between py-3" aria-label="Pagination">
        <div class="hidden sm:block">
            <div v-if="showMessage && message && message.show && !message.showCustomMessage" class="flex text-sm text-gray-700">
                <span>{{ displayMessage }}</span>
            </div>
        </div>
        <p v-if="message.showCustomMessage && showMessage" class="text-sm text-gray-700">
            <slot name="customMessage" />
        </p>
        <div v-if="currentPageItemsCount" class="flex flex-1 justify-between sm:justify-end">
            <span class="isolate inline-flex rounded-md shadow-xs">
                <button
                    aria-label="button"
                    type="button"
                    class="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-gray-600 ring-1 ring-inset ring-gray-300 hover:enabled:bg-gray-50 disabled:bg-gray-100"
                    :disabled="disableNavigation || isFirstPage()"
                    @click="changePage(-1)">
                    <span class="sr-only">Previous</span>
                    <ChevronLeftIcon class="h-5 w-5" />
                </button>
                <div
                    type="button"
                    class="relative -ml-px inline-flex items-center justify-center bg-white px-3 py-2 text-sm text-gray-600 ring-1 ring-inset ring-gray-300 w-[96px]">
                    {{ pageText }} {{ page + 1 }}
                </div>
                <button
                    aria-label="button"
                    type="button"
                    class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-600 ring-1 ring-inset ring-gray-300 hover:enabled:bg-gray-50 disabled:bg-gray-100"
                    :disabled="disableNavigation || isLastPage()"
                    @click="changePage(1)">
                    <span class="sr-only">Next</span>
                    <ChevronRightIcon class="h-5 w-5" />
                </button>
            </span>
        </div>
    </nav>
</template>

<script>
import _ from 'lodash';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';

export default {
    name: 'Pagination',
    components: {
        ChevronLeftIcon,
        ChevronRightIcon
    },
    props: {
        page: {
            type: Number,
            required: true
        },
        pageSize: {
            type: Number,
            required: true
        },
        currentPageItemsCount: {
            type: Number,
            default: 0
        },
        showMessage: {
            type: Boolean,
            default: true
        },
        disableNavigation: {
            type: Boolean,
            default: false
        },
        // When provided, totalItemsCount is used to display "X out of Y" results.
        // Otherwise, if totalItemsCount is nil and hasNext is provided, we show a simpler message.
        totalItemsCount: {
            type: Number,
            default: null
        },
        // New prop: hasNext indicates if there is a subsequent page.
        hasNext: {
            type: Boolean,
            default: null
        },
        message: {
            type: Object,
            default: () =>
                _.defaults(
                    {},
                    {
                        show: true,
                        showCustomMessage: false,
                        firstPart: 'Showing results',
                        middlePart: 'to',
                        lastPart: ''
                    }
                )
        },
        pageText: {
            type: String,
            default: 'Page'
        }
    },
    emits: ['pageChange'],
    data() {
        return {
            copied: false
        };
    },
    computed: {
        // Check if totalItemsCount is nil.
        isTotalItemsNil() {
            return _.isNil(this.totalItemsCount);
        },
        // Check if hasNext is provided (not nil).
        hasNextProvided() {
            return !_.isNil(this.hasNext);
        },
        // Are we using the simpler hasNext mode?
        displaySimpleMessage() {
            return this.isTotalItemsNil && this.hasNextProvided;
        },
        // For total count mode, pageFrom is the starting index (1-indexed).
        pageFrom() {
            return this.page * this.pageSize + 1;
        },
        // pageTo is computed differently if totalItemsCount is provided,
        // or if using hasNext mode (where it sums up current loaded items).
        pageTo() {
            if (!this.isTotalItemsNil) {
                return Math.min(this.page * this.pageSize + this.pageSize, this.totalItemsCount);
            }
            return this.page * this.pageSize + this.currentPageItemsCount;
        },
        // If totalItemsCount is provided, then if it fits on one page, we consider it a small amount.
        // Otherwise, in hasNext mode, we check if the current page is full.
        smallAmountOfItems() {
            if (!this.isTotalItemsNil) {
                return this.totalItemsCount <= this.pageSize;
            }
            return this.currentPageItemsCount < this.pageSize;
        },
        // Build the display message string based on mode.
        displayMessage() {
            if (this.displaySimpleMessage) {
                if (this.page === 0) {
                    return `Showing first ${this.pageTo} results`;
                } else {
                    return `Showing results ${this.pageFrom} to ${this.pageTo}`;
                }
            } else {
                const last =
                    !this.smallAmountOfItems && this.message.lastPart
                        ? this.message.lastPart
                        : !this.smallAmountOfItems && !_.isNil(this.totalItemsCount)
                          ? `of ${this.totalItemsCount}`
                          : '';
                return `${this.message.firstPart} ${this.pageFrom} ${this.message.middlePart} ${this.pageTo} ${last}`.trim();
            }
        },
        // For totalItemsCount mode, if message.lastPart is not set then we append "of X".
        lastPart() {
            if (this.message?.lastPart) return this.message.lastPart;
            if (!this.isTotalItemsNil && !this.smallAmountOfItems) {
                return `of ${this.totalItemsCount}`;
            }
            return '';
        }
    },
    methods: {
        changePage(increment) {
            if ((this.isFirstPage() && increment < 0) || (this.isLastPage() && increment > 0)) {
                return;
            }
            this.$emit('pageChange', this.page + increment);
        },
        isFirstPage() {
            return this.page === 0;
        },
        // For isLastPage, we use totalItemsCount if provided;
        // otherwise if hasNext is explicitly provided, we rely on its value,
        // and if neither is available we fallback on the currentPageItemsCount.
        isLastPage() {
            if (!this.isTotalItemsNil) {
                return this.totalItemsCount <= (this.page + 1) * this.pageSize;
            } else if (this.hasNextProvided) {
                return !this.hasNext;
            }
            return this.currentPageItemsCount < this.pageSize;
        }
    }
};
</script>

<style scoped></style>
